import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormCommunicationPreference,
  CIPXQuoteRequestIntakeFormCommunicationsContact,
  CIPXQuoteRequestIntakeFormCommunicationsContactType,
  CIPXQuoteRequestIntakeFormCommunicationsContactTypeType,
  CIPXQuoteRequestIntakeFormPrincipalContact,
} from '../../intake-form.types';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { CURRENT_USER_TOKEN, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import { MatDialog } from '@angular/material/dialog';
import { IntakeFormContactsDialogComponent } from './intake-form-contacts-dialog.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { pairwise, startWith, Subject, takeUntil } from 'rxjs';
import { isKnownContactEmail, isKnownContactPhone, isServiceEmail } from '@sbd-doug-frontend/shared/lib/public-validators';
import { boolsFromChannelValue, prefChannels } from '@sbd-doug-frontend/shared/lib/types/active-communication-contacts';

type QuoteReadyTypeCode = 'quote_is_ready' | 'renewal_quote_is_ready';

/**
 * The account user's saved setting for the given type code, or null when never saved.
 * communication_setting_value is the audience (0=None, 1=Customer, 2=Me, 3=Both);
 * communication_channel_value is the notify-me-by bitmask stored on the row.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function quoteReadySetting(cu: any, typeCode: QuoteReadyTypeCode): any | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settings: any[] = cu?.user_communication_settings ?? [];
  return settings.find((s) => s?.communication_setting_type_code === typeCode) ?? null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function settingChannels(setting: any, cu: any): { email: boolean; phone: boolean; sms: boolean } {
  // 0 is unset (email-off on a stale row must not hide a phone-only user-level mask)
  const val = Number(cu?.communication_channel_value) || Number(setting?.communication_channel_value) || 0;
  return boolsFromChannelValue(val);
}

function prefOrAccountChannels(
  pref: CIPXQuoteRequestIntakeFormCommunicationPreference | undefined,
  audienceBit: number,
  fallback: { email: boolean; phone: boolean; sms: boolean },
): { email: boolean; phone: boolean; sms: boolean } {
  if (!pref) return fallback;
  return prefChannels(pref, audienceBit);
}

function channelsWithContactInfo(
  channels: { email: boolean; phone: boolean; sms: boolean },
  email: unknown,
  phone: unknown,
): { email: boolean; phone: boolean; sms: boolean } {
  return {
    email: channels.email && isKnownContactEmail(email),
    phone: channels.phone && isKnownContactPhone(phone),
    sms: channels.sms,
  };
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    SharedComponentsModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  selector: 'cipx-quote-request-intake-form-notifications',
  template: `
    <div class="cipx-form-section">
      <fieldset>
        <legend class="cipx-form-section-title">When quotes are ready, notify:</legend>
        <div class="cipx-form-form-group flex flex-col gap-4">

          <!-- Account User -->
          @if (accountUser) {
            <div class="flex flex-col gap-1">
              <mat-checkbox
                [checked]="isContactEnabled(accountUser)"
                (change)="toggleContactEnabled(accountUser, $event.checked)">
                <span class="font-medium">{{ contactFullName(accountUser) }} <span class="font-normal text-gray-500">(you)</span></span>
              </mat-checkbox>
              <div class="pl-8 flex flex-col gap-1">
                <mat-checkbox
                  [checked]="fc(accountUser, 'email_enabled').value"
                  [disabled]="!isContactEnabled(accountUser)"
                  (change)="setChannel(accountUser, 'email_enabled', $event.checked)">
                  {{ fc(accountUser, 'email').value || '—' }}
                </mat-checkbox>
                <mat-checkbox
                  [checked]="fc(accountUser, 'phone_enabled').value"
                  [disabled]="!isContactEnabled(accountUser)"
                  (change)="setChannel(accountUser, 'phone_enabled', $event.checked)">
                  {{ formatPhoneDisplay(fc(accountUser, 'phone').value) }}
                </mat-checkbox>
              </div>
            </div>
          }

          <!-- User contacts (owners / principal) -->
          @for (contact of userContacts; track contact) {
            <div class="flex flex-col gap-1">
              <mat-checkbox
                [checked]="isContactEnabled(contact)"
                (change)="toggleContactEnabled(contact, $event.checked)">
                <span class="font-medium">{{ contactFullName(contact) }}</span>
              </mat-checkbox>
              <div class="pl-8 flex flex-col gap-1">
                <div class="flex items-center gap-2">
                  <mat-checkbox
                    class="shrink-0"
                    [checked]="fc(contact, 'email_enabled').value"
                    [disabled]="!isContactEnabled(contact)"
                    (change)="setChannel(contact, 'email_enabled', $event.checked)"></mat-checkbox>
                  <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-64">
                    <input matInput placeholder="Email" [formControl]="fc(contact, 'email')" />
                  </mat-form-field>
                </div>
                <div class="flex items-center gap-2">
                  <mat-checkbox
                    class="shrink-0"
                    [checked]="fc(contact, 'phone_enabled').value"
                    [disabled]="!isContactEnabled(contact)"
                    (change)="setChannel(contact, 'phone_enabled', $event.checked)"></mat-checkbox>
                  <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-64">
                    <input matInput placeholder="Phone" [formControl]="fc(contact, 'phone')" mask="(000) 000-0000" [dropSpecialCharacters]="false" />
                  </mat-form-field>
                </div>
              </div>
            </div>
          }

          <!-- Listener contacts -->
          @for (contact of listenerContacts; track contact) {
            <div class="flex flex-col gap-1">
              <mat-checkbox
                [checked]="isContactEnabled(contact)"
                (change)="toggleContactEnabled(contact, $event.checked)">
                <span class="font-medium">{{ contactFullName(contact) }}</span>
                @if (fc(contact, 'company_label').value) {
                  <span class="font-normal text-gray-500"> ({{ fc(contact, 'company_label').value }})</span>
                }
              </mat-checkbox>
              <div class="pl-8 flex flex-col gap-1">
                <mat-checkbox
                  [checked]="fc(contact, 'email_enabled').value"
                  [disabled]="!isContactEnabled(contact)"
                  (change)="setChannel(contact, 'email_enabled', $event.checked)">
                  {{ fc(contact, 'email').value || '—' }}
                </mat-checkbox>
                <mat-checkbox
                  [checked]="fc(contact, 'phone_enabled').value"
                  [disabled]="!isContactEnabled(contact)"
                  (change)="setChannel(contact, 'phone_enabled', $event.checked)">
                  {{ formatPhoneDisplay(fc(contact, 'phone').value) }}
                </mat-checkbox>
              </div>
            </div>
          }

          <div>
            <button mat-button color="primary" type="button" (click)="addListenerModal()">
              Add Another Contact
            </button>
          </div>
        </div>
      </fieldset>
    </div>

    <mat-divider class="my-4"></mat-divider>

    <div class="cipx-form-section">
      <fieldset>
        <div class="cipx-form-form-group">
          <mat-checkbox [formControl]="useSameForRenewalsCtrl">
            Use the same settings for renewals
          </mat-checkbox>
        </div>
      </fieldset>
    </div>

    @if (!useSameForRenewalsCtrl.value) {
      <div class="cipx-form-section">
        <fieldset>
          <legend class="cipx-form-section-title">When renewals are ready, notify:</legend>
          <div class="cipx-form-form-group flex flex-col gap-4">

            @if (accountUser) {
              <div class="flex flex-col gap-1">
                <mat-checkbox
                  [checked]="isRenewalContactEnabled(accountUser)"
                  (change)="toggleRenewalContactEnabled(accountUser, $event.checked)">
                  <span class="font-medium">{{ contactFullName(accountUser) }} <span class="font-normal text-gray-500">(you)</span></span>
                </mat-checkbox>
                <div class="pl-8 flex flex-col gap-1">
                  <mat-checkbox
                    [checked]="fc(accountUser, 'renewal_email_enabled').value"
                    [disabled]="!isRenewalContactEnabled(accountUser)"
                    (change)="setChannel(accountUser, 'renewal_email_enabled', $event.checked)">
                    {{ fc(accountUser, 'email').value || '—' }}
                  </mat-checkbox>
                  <mat-checkbox
                    [checked]="fc(accountUser, 'renewal_phone_enabled').value"
                    [disabled]="!isRenewalContactEnabled(accountUser)"
                    (change)="setChannel(accountUser, 'renewal_phone_enabled', $event.checked)">
                    {{ formatPhoneDisplay(fc(accountUser, 'phone').value) }}
                  </mat-checkbox>
                </div>
              </div>
            }

            @for (contact of userContacts; track contact) {
              <div class="flex flex-col gap-1">
                <mat-checkbox
                  [checked]="isRenewalContactEnabled(contact)"
                  (change)="toggleRenewalContactEnabled(contact, $event.checked)">
                  <span class="font-medium">{{ contactFullName(contact) }}</span>
                </mat-checkbox>
                <div class="pl-8 flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <mat-checkbox
                      class="shrink-0"
                      [checked]="fc(contact, 'renewal_email_enabled').value"
                      [disabled]="!isRenewalContactEnabled(contact)"
                      (change)="setChannel(contact, 'renewal_email_enabled', $event.checked)"></mat-checkbox>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-64">
                      <input matInput placeholder="Email" [formControl]="fc(contact, 'email')" />
                    </mat-form-field>
                  </div>
                  <div class="flex items-center gap-2">
                    <mat-checkbox
                      class="shrink-0"
                      [checked]="fc(contact, 'renewal_phone_enabled').value"
                      [disabled]="!isRenewalContactEnabled(contact)"
                      (change)="setChannel(contact, 'renewal_phone_enabled', $event.checked)"></mat-checkbox>
                    <mat-form-field appearance="outline" subscriptSizing="dynamic" class="w-64">
                      <input matInput placeholder="Phone" [formControl]="fc(contact, 'phone')" mask="(000) 000-0000" [dropSpecialCharacters]="false" />
                    </mat-form-field>
                  </div>
                </div>
              </div>
            }

            @for (contact of listenerContacts; track contact) {
              <div class="flex flex-col gap-1">
                <mat-checkbox
                  [checked]="isRenewalContactEnabled(contact)"
                  (change)="toggleRenewalContactEnabled(contact, $event.checked)">
                  <span class="font-medium">{{ contactFullName(contact) }}</span>
                  @if (fc(contact, 'company_label').value) {
                    <span class="font-normal text-gray-500"> ({{ fc(contact, 'company_label').value }})</span>
                  }
                </mat-checkbox>
                <div class="pl-8 flex flex-col gap-1">
                  <mat-checkbox
                    [checked]="fc(contact, 'renewal_email_enabled').value"
                    [disabled]="!isRenewalContactEnabled(contact)"
                    (change)="setChannel(contact, 'renewal_email_enabled', $event.checked)">
                    {{ fc(contact, 'email').value || '—' }}
                  </mat-checkbox>
                  <mat-checkbox
                    [checked]="fc(contact, 'renewal_phone_enabled').value"
                    [disabled]="!isRenewalContactEnabled(contact)"
                    (change)="setChannel(contact, 'renewal_phone_enabled', $event.checked)">
                    {{ formatPhoneDisplay(fc(contact, 'phone').value) }}
                  </mat-checkbox>
                </div>
              </div>
            }

          </div>
        </fieldset>
      </div>
    }
  `,
})
export class CIPXQuoteRequestIntakeFormNotificationsComponent implements OnInit, OnDestroy {
  private _intakeService = inject(CIPXQuoteRequestIntakeFormService);
  private _currentUser = inject<SharedUserService | null>(CURRENT_USER_TOKEN, { optional: true });
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _dialog = inject(MatDialog);
  private _destroy$ = new Subject<void>();

  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;

  get contactsArray(): FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>> {
    return this.quoteRequestForm.controls.communications.controls.contacts as unknown as FormArray;
  }

  get useSameForRenewalsCtrl(): FormControl<boolean> {
    return this.quoteRequestForm.controls.communications.controls.use_same_for_renewals as FormControl<boolean>;
  }

  get accountUser(): FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact> | null {
    return (this.contactsArray.controls.find(
      (c) => c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser
    ) as FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>) ?? null;
  }

  get userContacts(): FormGroup[] {
    return this.contactsArray.controls.filter(
      (c) => c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.User
    ) as FormGroup[];
  }

  get listenerContacts(): FormGroup[] {
    return this.contactsArray.controls.filter(
      (c) => c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener
    ) as FormGroup[];
  }

  fc(group: FormGroup, path: string): FormControl {
    return group.get(path) as FormControl;
  }

  contactFullName(contact: FormGroup): string {
    const first = String(contact.get('first_name')?.value ?? '');
    const last = String(contact.get('last_name')?.value ?? '');
    return [first, last].filter(Boolean).join(' ') || '—';
  }

  isContactEnabled(contact: FormGroup): boolean {
    return Boolean(contact.get('email_enabled')?.value) || Boolean(contact.get('phone_enabled')?.value);
  }

  isRenewalContactEnabled(contact: FormGroup): boolean {
    return Boolean(contact.get('renewal_email_enabled')?.value) || Boolean(contact.get('renewal_phone_enabled')?.value);
  }

  /**
   * User-driven checkbox change: sets the control and marks the contact group
   * dirty so re-derivation on step re-entry knows not to clobber the choice.
   */
  setChannel(contact: FormGroup, controlName: string, checked: boolean): void {
    (contact.get(controlName) as FormControl)?.setValue(checked);
    contact.markAsDirty();
  }

  toggleContactEnabled(contact: FormGroup, enabled: boolean): void {
    if (!enabled) {
      (contact.get('email_enabled') as FormControl)?.setValue(false);
      (contact.get('phone_enabled') as FormControl)?.setValue(false);
      (contact.get('sms_enabled') as FormControl)?.setValue(false);
    } else {
      const hasEmail = Boolean(String(contact.get('email')?.value ?? '').trim());
      const hasPhone = Boolean(String(contact.get('phone')?.value ?? '').replace(/\D/g, ''));
      (contact.get('email_enabled') as FormControl)?.setValue(hasEmail);
      (contact.get('phone_enabled') as FormControl)?.setValue(hasPhone);
    }
    contact.markAsDirty();
    this._changeDetectorRef.markForCheck();
  }

  toggleRenewalContactEnabled(contact: FormGroup, enabled: boolean): void {
    if (!enabled) {
      (contact.get('renewal_email_enabled') as FormControl)?.setValue(false);
      (contact.get('renewal_phone_enabled') as FormControl)?.setValue(false);
    } else {
      const hasEmail = Boolean(String(contact.get('email')?.value ?? '').trim());
      const hasPhone = Boolean(String(contact.get('phone')?.value ?? '').replace(/\D/g, ''));
      (contact.get('renewal_email_enabled') as FormControl)?.setValue(hasEmail);
      (contact.get('renewal_phone_enabled') as FormControl)?.setValue(hasPhone);
    }
    contact.markAsDirty();
    this._changeDetectorRef.markForCheck();
  }

  public formatPhoneDisplay(value: string | null | undefined): string {
    const digits = String(value ?? '').replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(value ?? '') || '—';
  }

  private _normalizeText(value: unknown): string {
    return String(value ?? '').trim().toLowerCase();
  }

  /**
   * Whether the person is the portal account user, who already has their own
   * "you" card — never show them a second time as an owner/listener contact.
   */
  private _isAccountUserIdentity(person: { first?: unknown; last?: unknown; email?: unknown }): boolean {
    const cu = this._currentUser?.current?.();
    if (!cu) return false;

    const email = this._normalizeText(person.email);
    if (email) {
      const knownEmails = [cu.email, this.accountUser?.get('email')?.value]
        .map((e) => this._normalizeText(e))
        .filter(Boolean);
      if (knownEmails.includes(email)) return true;
    }

    const first = this._normalizeText(person.first);
    const last = this._normalizeText(person.last);
    return Boolean(first && last)
      && first === this._normalizeText(cu.firstName)
      && last === this._normalizeText(cu.lastName);
  }

  private _ownerRaw(owner: AbstractControl, name: string): string {
    const ctrl = owner.get(name);
    return String((ctrl as FormControl | null)?.getRawValue() ?? ctrl?.value ?? '');
  }

  private _isOwnerContact(
    contact: { user_email_address?: string; user_first_name?: string; user_last_name?: string; user_phone?: string },
    ownersArray: FormArray
  ): boolean {
    const contactEmail = this._normalizeText(contact.user_email_address);
    const contactFirst = this._normalizeText(contact.user_first_name);
    const contactLast = this._normalizeText(contact.user_last_name);
    const contactPhone = String(contact.user_phone ?? '').replace(/\D/g, '');

    return ownersArray.controls.some((ownerCtrl) => {
      const ownerEmail = this._normalizeText(this._ownerRaw(ownerCtrl, 'owner_email'));
      if (contactEmail && ownerEmail && contactEmail === ownerEmail) return true;
      const ownerFirst = this._normalizeText(this._ownerRaw(ownerCtrl, 'owner_first_name'));
      const ownerLast = this._normalizeText(this._ownerRaw(ownerCtrl, 'owner_last_name'));
      const ownerPhone = this._ownerRaw(ownerCtrl, 'owner_phone').replace(/\D/g, '');
      const nameMatches = contactFirst && contactLast && contactFirst === ownerFirst && contactLast === ownerLast;
      if (!nameMatches) return false;
      const phoneMatches = contactPhone && ownerPhone && contactPhone === ownerPhone;
      return phoneMatches || !contactPhone || !ownerPhone;
    });
  }

  private _matchingOwner(
    pc: CIPXQuoteRequestIntakeFormPrincipalContact,
    ownersArray: FormArray
  ) {
    const pcFirst = this._normalizeText(pc.user_first_name);
    const pcLast = this._normalizeText(pc.user_last_name);
    const pcPhone = String(pc.user_phone ?? '').replace(/\D/g, '');

    return ownersArray.controls.find((ownerCtrl) => {
      const ownerFirst = this._normalizeText(this._ownerRaw(ownerCtrl, 'owner_first_name'));
      const ownerLast = this._normalizeText(this._ownerRaw(ownerCtrl, 'owner_last_name'));
      if (!(pcFirst && pcLast && pcFirst === ownerFirst && pcLast === ownerLast)) return false;
      const ownerPhone = this._ownerRaw(ownerCtrl, 'owner_phone').replace(/\D/g, '');
      const phoneMatches = pcPhone && ownerPhone && pcPhone === ownerPhone;
      return phoneMatches || !pcPhone || !ownerPhone;
    });
  }

  private _getCustomerEmail(
    pc: CIPXQuoteRequestIntakeFormPrincipalContact,
    ownersArray: FormArray
  ): string {
    const pcEmail = String(pc.user_email_address ?? '');
    if (isKnownContactEmail(pcEmail)) return pcEmail;

    const matchingOwner = this._matchingOwner(pc, ownersArray);
    if (matchingOwner) {
      const ownerEmail = this._ownerRaw(matchingOwner, 'owner_email');
      if (isKnownContactEmail(ownerEmail)) return ownerEmail;
    }
    return isServiceEmail(pcEmail) ? '' : pcEmail;
  }

  private _getCustomerPhone(
    pc: CIPXQuoteRequestIntakeFormPrincipalContact,
    ownersArray: FormArray
  ): string {
    const pcPhone = String(pc.user_phone ?? '');
    if (isKnownContactPhone(pcPhone)) return pcPhone;

    const matchingOwner = this._matchingOwner(pc, ownersArray);
    if (matchingOwner) {
      const ownerPhone = this._ownerRaw(matchingOwner, 'owner_phone');
      if (isKnownContactPhone(ownerPhone)) return ownerPhone;
    }
    return pcPhone;
  }

  private _buildContactGroup(
    contactType: CIPXQuoteRequestIntakeFormCommunicationsContactTypeType,
    fields: {
      user_id: number | null;
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
      company_label?: string;
      emailEditable: boolean;
      phoneEditable?: boolean;
    },
    channels: { email: boolean; phone: boolean; sms: boolean },
    renewalChannels: { email: boolean; phone: boolean }
  ): FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact> {
    const emailField = new FormControl(fields.email, [Validators.email]);
    const phoneField = new FormControl(fields.phone);
    const emailEnabled = new FormControl<boolean>(channels.email, { nonNullable: true });
    const phoneEnabled = new FormControl<boolean>(channels.phone, { nonNullable: true });
    const renewalEmailEnabled = new FormControl<boolean>(renewalChannels.email, { nonNullable: true });
    const renewalPhoneEnabled = new FormControl<boolean>(renewalChannels.phone, { nonNullable: true });

    // Each field is bound to an input in both the new and renewal sections. User typing
    // updates the control with emitModelToViewChange:false, which skips the other input's
    // view — echo the value back so both inputs always display it.
    const mirrorViews = (ctrl: FormControl) =>
      ctrl.valueChanges.pipe(takeUntil(this._destroy$)).subscribe((v) => {
        ctrl.setValue(v, { emitEvent: false });
      });
    mirrorViews(emailField);
    mirrorViews(phoneField);

    const group = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
      contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(contactType, { nonNullable: true }),
      user_id: new FormControl<number | null>(fields.user_id),
      first_name: new FormControl(fields.first_name),
      last_name: new FormControl(fields.last_name),
      company_label: new FormControl(fields.company_label ?? ''),
      email: emailField,
      phone: phoneField,
      sms_phone: new FormControl(''),
      email_enabled: emailEnabled,
      phone_enabled: phoneEnabled,
      sms_enabled: new FormControl<boolean>(channels.sms, { nonNullable: true }),
      renewal_email_enabled: renewalEmailEnabled,
      renewal_phone_enabled: renewalPhoneEnabled,
      email_editable: new FormControl<boolean>(fields.emailEditable, { nonNullable: true }),
    });

    // Email field disabled/enabled state:
    // - If not editable (locked real email): always disabled regardless of checkboxes
    // - If editable: enabled while either the new or renewal email checkbox is on
    //   (the same control renders in both sections, so the value stays in sync)
    const syncEmail = () => {
      if (!fields.emailEditable) {
        emailField.clearValidators();
        emailField.disable({ emitEvent: false });
      } else if (emailEnabled.value || renewalEmailEnabled.value) {
        emailField.setValidators([Validators.required, Validators.email]);
        emailField.enable({ emitEvent: false });
      } else {
        emailField.clearValidators();
        emailField.disable({ emitEvent: false });
      }
      emailField.updateValueAndValidity({ emitEvent: false });
    };

    if (fields.emailEditable) {
      emailEnabled.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => syncEmail());
      renewalEmailEnabled.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => syncEmail());
    }
    syncEmail();

    const phoneEditable = fields.phoneEditable !== false;
    const syncPhone = () => {
      if (!phoneEditable) {
        phoneField.disable({ emitEvent: false });
      } else if (phoneEnabled.value || renewalPhoneEnabled.value) {
        phoneField.enable({ emitEvent: false });
      } else {
        phoneField.disable({ emitEvent: false });
      }
    };
    if (phoneEditable) {
      phoneEnabled.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => syncPhone());
      renewalPhoneEnabled.valueChanges.pipe(takeUntil(this._destroy$)).subscribe(() => syncPhone());
    }
    syncPhone();

    return group;
  }

  private _prefsForUser(
    preferences: CIPXQuoteRequestIntakeFormCommunicationPreference[],
    userId: number,
    typeCode: QuoteReadyTypeCode
  ): CIPXQuoteRequestIntakeFormCommunicationPreference | undefined {
    // Preferences come back one row per user x setting type; this screen only
    // configures the quote-ready notifications, so only those rows' channels apply.
    return preferences.find(
      (p) => Number(p.user_id) === userId && p.communication_setting_type_code === typeCode
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public buildAccountUserCommunications(cu: any): void {
    const newSetting = quoteReadySetting(cu, 'quote_is_ready');
    const renewalSetting = quoteReadySetting(cu, 'renewal_quote_is_ready');
    const newSettingChannels = settingChannels(newSetting, cu);
    const renewalSettingChannels = settingChannels(renewalSetting, cu);

    // Never-saved settings default to notifying the account user
    const enableAnnouncementsForMe = newSetting == null || (newSetting.communication_setting_value & 2) !== 0;
    const enableRenewalForMe = renewalSetting == null || (renewalSetting.communication_setting_value & 2) !== 0;

    const emailDefault = enableAnnouncementsForMe && newSettingChannels.email;
    const phoneDefault = enableAnnouncementsForMe && newSettingChannels.phone;
    const smsDefault = enableAnnouncementsForMe && newSettingChannels.sms;
    const renewalEmailDefault = enableRenewalForMe && renewalSettingChannels.email;
    const renewalPhoneDefault = enableRenewalForMe && renewalSettingChannels.phone;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const alternateEmail: any = cu?.user_info?.preferences?.notifications?.alternate_email ?? null;
    const alternateEmailEnabled = alternateEmail?.alternate_email_enabled === true;
    const alternateSelectedEmail = String(alternateEmail?.alternate_selected_email ?? '').trim();
    const emailAddr = alternateEmailEnabled && alternateSelectedEmail
      ? alternateSelectedEmail
      : String(cu?.email ?? '');
    const phone = String(cu?.phone ?? '');
    const userId = cu?.principal_user_id ? Number(cu.principal_user_id) : null;

    // Override with stored preferences from principal API if available
    const stored = this._intakeService.getStoredCommunicationData();
    const prefs = stored.communicationPreferences ?? [];
    let newChannels = { email: emailDefault, phone: phoneDefault, sms: smsDefault };
    let renewalChannels = { email: renewalEmailDefault, phone: renewalPhoneDefault };

    if (userId !== null && prefs.length > 0) {
      newChannels = prefOrAccountChannels(this._prefsForUser(prefs, userId, 'quote_is_ready'), 2, newChannels);
      const renewal = prefOrAccountChannels(
        this._prefsForUser(prefs, userId, 'renewal_quote_is_ready'),
        2,
        { ...renewalChannels, sms: false },
      );
      renewalChannels = { email: renewal.email, phone: renewal.phone };
    }
    newChannels = channelsWithContactInfo(newChannels, emailAddr, phone);
    renewalChannels = {
      email: renewalChannels.email && isKnownContactEmail(emailAddr),
      phone: renewalChannels.phone && isKnownContactPhone(phone),
    };

    const existing = this.accountUser;
    if (existing) {
      (existing.get('first_name') as FormControl)?.setValue(cu?.firstName ?? '');
      (existing.get('last_name') as FormControl)?.setValue(cu?.lastName ?? '');
      (existing.get('user_id') as FormControl)?.setValue(userId);
      (existing.get('email') as FormControl)?.setValue(emailAddr);
      (existing.get('phone') as FormControl)?.setValue(phone);
      // A dirty group means the user configured it this session (e.g. before
      // "request another for this principal") — keep their choices over re-derived defaults
      if (!existing.dirty) {
        (existing.get('email_enabled') as FormControl)?.setValue(newChannels.email);
        (existing.get('phone_enabled') as FormControl)?.setValue(newChannels.phone);
        (existing.get('sms_enabled') as FormControl)?.setValue(newChannels.sms);
        (existing.get('renewal_email_enabled') as FormControl)?.setValue(renewalChannels.email);
        (existing.get('renewal_phone_enabled') as FormControl)?.setValue(renewalChannels.phone);
      }
      this._changeDetectorRef.detectChanges();
      return;
    }

    const group = this._buildContactGroup(
      CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser,
      { user_id: userId, first_name: cu?.firstName ?? '', last_name: cu?.lastName ?? '', email: emailAddr, phone, emailEditable: false, phoneEditable: false },
      newChannels,
      renewalChannels
    );
    this.contactsArray.push(group);
    this._changeDetectorRef.detectChanges();
  }

  public ensureOwnerAndListenerContacts(): void {
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    const stored = this._intakeService.getStoredCommunicationData();
    // Contact rows are now preference-shaped (one per user x setting), so keep one per user
    const principalContacts: CIPXQuoteRequestIntakeFormPrincipalContact[] = [
      ...new Map((stored.principalContacts ?? []).map((pc) => [Number(pc.user_id), pc])).values(),
    ];
    const prefs = stored.communicationPreferences ?? [];

    const cu = this._currentUser?.current?.();
    if (!cu) return;

    const newSetting = quoteReadySetting(cu, 'quote_is_ready');
    const renewalSetting = quoteReadySetting(cu, 'renewal_quote_is_ready');
    const enableAnnouncementsForCustomer = newSetting != null && (newSetting.communication_setting_value & 1) !== 0;
    const enableRenewalForCustomer = renewalSetting != null && (renewalSetting.communication_setting_value & 1) !== 0;
    const newAccountChannels = settingChannels(newSetting, cu);
    const renewalAccountChannels = settingChannels(renewalSetting, cu);

    const accountUserId = this.accountUser?.get('user_id')?.value != null
      ? Number(this.accountUser.get('user_id')?.value)
      : null;

    // Channel choices the user made this session (dirty groups) must survive the
    // rebuild, e.g. across "request another for this principal"
    const preservationKeys = (ctrl: FormGroup): string[] => {
      const keys: string[] = [];
      const uid = ctrl.get('user_id')?.value as number | null;
      if (uid !== null) keys.push(`id:${uid}`);
      const first = this._normalizeText(ctrl.get('first_name')?.value);
      const last = this._normalizeText(ctrl.get('last_name')?.value);
      if (first && last) keys.push(`nm:${first}|${last}`);
      return keys;
    };
    const preservedChannels = new Map<string, {
      email: boolean; phone: boolean; sms: boolean; renewalEmail: boolean; renewalPhone: boolean;
    }>();
    for (const ctrl of this.contactsArray.controls as FormGroup[]) {
      const type = ctrl.get('contact_type')?.value ?? '';
      if (type === CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser || !ctrl.dirty) continue;
      const snapshot = {
        email: Boolean(ctrl.get('email_enabled')?.value),
        phone: Boolean(ctrl.get('phone_enabled')?.value),
        sms: Boolean(ctrl.get('sms_enabled')?.value),
        renewalEmail: Boolean(ctrl.get('renewal_email_enabled')?.value),
        renewalPhone: Boolean(ctrl.get('renewal_phone_enabled')?.value),
      };
      for (const key of preservationKeys(ctrl)) preservedChannels.set(key, snapshot);
    }

    const principalContactIds = new Set(
      principalContacts.map((pc) => Number(pc.user_id)).filter((id) => !Number.isNaN(id))
    );
    const contactUserIds = () =>
      new Set(
        this.contactsArray.controls
          .map((c) => c.get('user_id')?.value)
          .filter((id): id is number => id != null)
          .map((id) => Number(id))
      );

    // Keep the AU card and dialog-added listeners that are not already in principal_contacts
    for (let i = this.contactsArray.length - 1; i >= 0; i--) {
      const ctrl = this.contactsArray.at(i) as FormGroup;
      const type = ctrl.get('contact_type')?.value ?? '';
      if (type === CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser) continue;
      const userId = ctrl.get('user_id')?.value as number | null;
      const isDialogOnlyListener =
        type === CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener &&
        userId !== null &&
        !principalContactIds.has(Number(userId));
      if (!isDialogOnlyListener) this.contactsArray.removeAt(i);
    }

    if (principalContacts.length > 0) {
      for (const pc of principalContacts) {
        const userId = Number(pc.user_id);
        if (isNaN(userId)) continue;
        if (accountUserId !== null && userId === accountUserId) continue;
        if (this._isAccountUserIdentity({ first: pc.user_first_name, last: pc.user_last_name, email: pc.user_email_address })) continue;
        if (contactUserIds().has(userId)) continue;

        const email = this._getCustomerEmail(pc, ownersArray);
        const phone = this._getCustomerPhone(pc, ownersArray);
        const hasPhone = isKnownContactPhone(phone);

        const accountNew = {
          email: enableAnnouncementsForCustomer && newAccountChannels.email && Boolean(email),
          phone: enableAnnouncementsForCustomer && newAccountChannels.phone && hasPhone,
          sms: false,
        };
        const accountRenewal = {
          email: enableRenewalForCustomer && renewalAccountChannels.email && Boolean(email),
          phone: enableRenewalForCustomer && renewalAccountChannels.phone && hasPhone,
          sms: false,
        };
        const newChannels = channelsWithContactInfo(
          prefOrAccountChannels(
            this._prefsForUser(prefs, userId, 'quote_is_ready'),
            1,
            accountNew,
          ),
          email,
          phone,
        );
        const renewal = channelsWithContactInfo(
          prefOrAccountChannels(
            this._prefsForUser(prefs, userId, 'renewal_quote_is_ready'),
            1,
            accountRenewal,
          ),
          email,
          phone,
        );
        const renewalChannels = { email: renewal.email, phone: renewal.phone };

        const isOwner = this._isOwnerContact(pc, ownersArray);
        const contactType = isOwner
          ? CIPXQuoteRequestIntakeFormCommunicationsContactType.User
          : CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener;

        const emailEditable = isOwner && !isKnownContactEmail(email);
        const phoneEditable = isOwner && !isKnownContactPhone(phone);

        const group = this._buildContactGroup(
          contactType,
          { user_id: userId, first_name: pc.user_first_name, last_name: pc.user_last_name, email, phone, company_label: pc.user_company_label ?? '', emailEditable, phoneEditable },
          newChannels,
          renewalChannels
        );
        this.contactsArray.push(group);
      }
      // Append any owners added this session that have no principalContacts entry
      for (let i = 0; i < ownersArray.length; i++) {
        const owner = ownersArray.at(i);
        const alreadyCovered = principalContacts.some((pc) =>
          this._isOwnerContact(pc, new FormArray([owner as FormGroup]))
        );
        if (alreadyCovered) continue;

        const firstName = this._ownerRaw(owner, 'owner_first_name');
        const lastName  = this._ownerRaw(owner, 'owner_last_name');
        const rawEmail  = this._ownerRaw(owner, 'owner_email');
        if (this._isAccountUserIdentity({ first: firstName, last: lastName, email: rawEmail })) continue;
        const email     = isKnownContactEmail(rawEmail) ? rawEmail : (isServiceEmail(rawEmail) ? '' : rawEmail);
        const phone     = this._ownerRaw(owner, 'owner_phone');
        const hasPhone  = isKnownContactPhone(phone);

        const newChannels = {
          email: enableAnnouncementsForCustomer && newAccountChannels.email,
          phone: enableAnnouncementsForCustomer && newAccountChannels.phone && hasPhone,
          sms: false,
        };
        const renewalChannels = {
          email: enableRenewalForCustomer && renewalAccountChannels.email,
          phone: enableRenewalForCustomer && renewalAccountChannels.phone && hasPhone,
        };

        const group = this._buildContactGroup(
          CIPXQuoteRequestIntakeFormCommunicationsContactType.User,
          {
            user_id: null,
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            emailEditable: true,
            phoneEditable: true,
          },
          newChannels,
          renewalChannels
        );
        this.contactsArray.push(group);
      }
    } else {
      // Fallback: build from owners FormArray (no user_id)
      let primaryOwnerAdded = false;
      for (let i = 0; i < ownersArray.length; i++) {
        const owner = ownersArray.at(i);
        const firstName = this._ownerRaw(owner, 'owner_first_name');
        const lastName = this._ownerRaw(owner, 'owner_last_name');
        const rawEmail = this._ownerRaw(owner, 'owner_email');
        if (this._isAccountUserIdentity({ first: firstName, last: lastName, email: rawEmail })) continue;
        const email = isServiceEmail(rawEmail) ? '' : rawEmail;
        const phone = this._ownerRaw(owner, 'owner_phone');
        const hasPhone = isKnownContactPhone(phone);

        const newChannels = {
          email: enableAnnouncementsForCustomer && newAccountChannels.email,
          phone: enableAnnouncementsForCustomer && newAccountChannels.phone && hasPhone,
          sms: false,
        };
        const renewalChannels = {
          email: enableRenewalForCustomer && renewalAccountChannels.email,
          phone: enableRenewalForCustomer && renewalAccountChannels.phone && hasPhone,
        };

        const contactType = !primaryOwnerAdded
          ? CIPXQuoteRequestIntakeFormCommunicationsContactType.User
          : CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener;
        primaryOwnerAdded = true;

        const group = this._buildContactGroup(
          contactType,
          { user_id: null, first_name: firstName, last_name: lastName, email, phone, emailEditable: true, phoneEditable: true },
          newChannels,
          renewalChannels
        );
        this.contactsArray.push(group);
      }
    }

    // Restore the user's session choices onto rebuilt contacts and re-mark them
    // dirty so they keep surviving later rebuilds
    if (preservedChannels.size > 0) {
      for (const ctrl of this.contactsArray.controls as FormGroup[]) {
        const type = ctrl.get('contact_type')?.value ?? '';
        if (type === CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser) continue;
        const saved = preservationKeys(ctrl).map((k) => preservedChannels.get(k)).find(Boolean);
        if (!saved) continue;
        (ctrl.get('email_enabled') as FormControl)?.setValue(saved.email);
        (ctrl.get('phone_enabled') as FormControl)?.setValue(saved.phone);
        (ctrl.get('sms_enabled') as FormControl)?.setValue(saved.sms);
        (ctrl.get('renewal_email_enabled') as FormControl)?.setValue(saved.renewalEmail);
        (ctrl.get('renewal_phone_enabled') as FormControl)?.setValue(saved.renewalPhone);
        ctrl.markAsDirty();
      }
    }

    this._changeDetectorRef.detectChanges();
  }

  public addListenerModal(): void {
    const preselectedIds = this.listenerContacts
      .map((g) => g.get('user_id')?.value as number | null)
      .filter((id): id is number => id !== null);

    const ref = this._dialog.open(IntakeFormContactsDialogComponent, {
      width: '90vw',
      maxWidth: '100vw',
      height: '90vh',
      maxHeight: '90vh',
      data: { preselectedPrincipalContactIds: preselectedIds },
      disableClose: true,
      panelClass: 'cipx-intake-contacts-dialog',
    });

    ref.afterClosed().pipe(takeUntil(this._destroy$)).subscribe((result) => {
      if (!result?.selected || !Array.isArray(result.selected)) return;

      const cu = this._currentUser?.current?.();
      const newSetting = quoteReadySetting(cu, 'quote_is_ready');
      const renewalSetting = quoteReadySetting(cu, 'renewal_quote_is_ready');
      const newSettingChannels = settingChannels(newSetting, cu);
      const renewalSettingChannels = settingChannels(renewalSetting, cu);
      const customerForNew = newSetting != null && (newSetting.communication_setting_value & 1) !== 0;
      const customerForRenewal = renewalSetting != null && (renewalSetting.communication_setting_value & 1) !== 0;

      const emailDefault = newSettingChannels.email && customerForNew;
      const phoneDefault = newSettingChannels.phone && customerForNew;
      const announcementDefaults = (emailDefault || phoneDefault)
        ? { email: emailDefault, phone: phoneDefault }
        : { email: true, phone: false };

      const renewalEmailDefault = renewalSettingChannels.email && customerForRenewal;
      const renewalPhoneDefault = renewalSettingChannels.phone && customerForRenewal;
      const renewalDefaults = (renewalEmailDefault || renewalPhoneDefault)
        ? { email: renewalEmailDefault, phone: renewalPhoneDefault }
        : { email: true, phone: false };

      const selected = result.selected as Array<{
        user_id: number;
        user_first_name: string;
        user_last_name: string;
        user_email_address: string;
        user_phone: string;
        user_company_label?: string;
      }>;
      const selectedIds = new Set(selected.map((s) => s.user_id));

      // Disable removed listeners
      for (let i = 0; i < this.contactsArray.length; i++) {
        const ctrl = this.contactsArray.at(i) as FormGroup;
        if (ctrl.get('contact_type')?.value !== CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener) continue;
        const id = ctrl.get('user_id')?.value as number | null;
        if (id === null || selectedIds.has(id)) continue;
        (ctrl.get('email_enabled') as FormControl)?.setValue(false);
        (ctrl.get('phone_enabled') as FormControl)?.setValue(false);
        (ctrl.get('renewal_email_enabled') as FormControl)?.setValue(false);
        (ctrl.get('renewal_phone_enabled') as FormControl)?.setValue(false);
      }

      // Add newly selected listeners
      const existingIds = new Set(
        this.listenerContacts.map((g) => g.get('user_id')?.value).filter((id): id is number => id !== null)
      );
      const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;

      selected
        .filter((c) => !existingIds.has(c.user_id))
        .forEach((c) => {
          const email = this._getCustomerEmail(
            { user_id: c.user_id, user_first_name: c.user_first_name, user_last_name: c.user_last_name, user_email_address: c.user_email_address, user_phone: c.user_phone, user_status: '' },
            ownersArray
          );
          const group = this._buildContactGroup(
            CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener,
            { user_id: c.user_id, first_name: c.user_first_name, last_name: c.user_last_name, email, phone: c.user_phone, company_label: c.user_company_label ?? '', emailEditable: false, phoneEditable: false },
            { email: announcementDefaults.email, phone: announcementDefaults.phone, sms: false },
            { email: renewalDefaults.email, phone: renewalDefaults.phone }
          );
          this.contactsArray.push(group);
        });

      this._changeDetectorRef.detectChanges();
    });
  }

  ngOnInit(): void {
    this._currentUser?.get()
      .pipe(takeUntil(this._destroy$))
      .subscribe((cu) => {
        this.buildAccountUserCommunications(cu);
        this.ensureOwnerAndListenerContacts();
        this._syncUseSameForRenewals();
      });

    // Sync owner email changes into matching notification contact
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    ownersArray.valueChanges
      .pipe(startWith(ownersArray.getRawValue()), pairwise(), takeUntil(this._destroy$))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe(([prevOwners, currOwners]: [any[], any[]]) => {
        const len = Math.max(prevOwners.length, currOwners.length);
        for (let i = 0; i < len; i++) {
          const prev = prevOwners[i] || {};
          const curr = currOwners[i] || {};
          const prevEmail = String(prev?.owner_email ?? '');
          const currEmail = String(curr?.owner_email ?? '');
          if (prevEmail === currEmail || !currEmail || isServiceEmail(currEmail)) continue;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const findContact = (snap: any) => {
            const firstN = this._normalizeText(snap?.owner_first_name);
            const lastN = this._normalizeText(snap?.owner_last_name);
            const emailN = this._normalizeText(snap?.owner_email);
            const phoneN = String(snap?.owner_phone ?? '').replace(/\D/g, '');
            return this.contactsArray.controls.find((ctrl) => {
              const cFirst = this._normalizeText(ctrl.get('first_name')?.value);
              const cLast = this._normalizeText(ctrl.get('last_name')?.value);
              const cEmail = this._normalizeText(ctrl.get('email')?.value);
              const cPhone = String(ctrl.get('phone')?.value ?? '').replace(/\D/g, '');
              if (emailN && cEmail && emailN === cEmail) return true;
              const nameMatches = firstN && lastN && firstN === cFirst && lastN === cLast;
              if (!nameMatches) return false;
              const phoneMatches = phoneN && cPhone && phoneN === cPhone;
              return phoneMatches || !phoneN || !cPhone;
            });
          };

          const matchingContact = findContact(prev) || findContact(curr);
          if (!matchingContact) continue;

          const emailCtrl = matchingContact.get('email') as FormControl | null;
          if (!emailCtrl || emailCtrl.value === currEmail) continue;

          const wasDisabled = emailCtrl.disabled;
          if (wasDisabled) emailCtrl.enable({ emitEvent: false });
          emailCtrl.setValue(currEmail, { emitEvent: false });
          emailCtrl.markAsDirty();
          emailCtrl.updateValueAndValidity({ emitEvent: false });
          if (wasDisabled) emailCtrl.disable({ emitEvent: false });
        }
      });
  }

  /**
   * When any contact's renewal channels differ from its new-quote channels (from saved
   * preferences or account-level defaults), uncheck "use the same settings for renewals"
   * so the differing renewal state is visible and preserved on submit.
   * Never forces it back to true.
   */
  private _syncUseSameForRenewals(): void {
    if (!this.useSameForRenewalsCtrl.value) return;

    const anyDiffer = this.contactsArray.controls.some((ctrl) =>
      Boolean(ctrl.get('email_enabled')?.value) !== Boolean(ctrl.get('renewal_email_enabled')?.value) ||
      Boolean(ctrl.get('phone_enabled')?.value) !== Boolean(ctrl.get('renewal_phone_enabled')?.value)
    );

    if (anyDiffer) {
      this.useSameForRenewalsCtrl.setValue(false, { emitEvent: false });
      this._changeDetectorRef.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
